import type { GuideChapter } from '../types'
import { n } from '../types'

export const intranet: GuideChapter = {
  id: 'intranet',
  title: 'Intranet',
  icon: '📣',
  intro: `Husets egen side: opslag fra kontoret, begivenheder med tilmelding og afstemning om datoen, interne opgaver, arbejdsgrupper, telefonlisten over kollegerne og genveje til de andre systemer. Det hele ligger under "Intranet" øverst i menuen. Øverst på siden står fire runde knapper - "Hverdag", "Fællesskab", "Personale" og "Info" - og under den valgte gruppe står dens funktioner. Et rødt tal på en gruppe betyder, at der er nyt i en af dens funktioner.`,
  sections: [
    {
      id: 'intranet-news',
      title: 'Læs opslagene fra kontoret',
      icon: '📣',
      role: 'Medarbejder',
      module: 'intranet',
      where: `"Intranet" → "Hverdag" → "Nyheder"`,
      intro: `Nyt fra kontoret står her: nye regler, lukkedage, en ny kollega. De vigtigste er hængt op øverst med en knappenål.`,
      sketch: {
        device: 'phone',
        title: 'Intranet',
        nav: true,
        blocks: [
          { t: 'text', text: 'Nyheder · Kalender · Telefonliste · Genveje', n: 1 },
          {
            t: 'card',
            title: '📌 Nye regler for kørsel i firmabil',
            children: [
              { t: 'text', text: 'Simon · 23.09.2026' },
              { t: 'text', text: 'Fra 1. oktober skal kørebogen udfyldes hver dag.' },
              { t: 'btn', text: 'Jeg har læst det', primary: true, n: 2 }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Kvittér for et opslag',
          steps: [
            n(1, `Åbn "Intranet" og bliv stående på "Nyheder".`),
            n(2, `Står der "Jeg har læst det", så tryk på knappen, når du har læst opslaget. Kontoret kan se, hvem der mangler.`)
          ]
        }
      ],
      tips: [
        `Et opslag med knappenål er hængt op øverst, fordi det skal blive stående.`,
        `Står der et rødt tal ved "Intranet" i menuen, er der noget nyt. Tallet forsvinder, når du har åbnet siden - men et opslag, der skal kvitteres for, bliver stående i tallet, til du har trykket.`
      ]
    },
    {
      id: 'intranet-news-write',
      title: 'Skriv et opslag til huset',
      icon: '✍️',
      role: 'Administrator',
      module: 'intranet',
      where: `"Intranet" → "Nyheder" → "Nyt opslag"`,
      intro: `Skriv til hele huset, læg billeder på, og vælg om alle skal kvittere for at have læst det.`,
      flows: [
        {
          title: 'Slå noget op',
          steps: [
            `Tryk "Nyt opslag", og skriv overskrift og tekst.`,
            `Sæt flueben ved "Hæng op øverst", hvis det skal blive stående.`,
            `Sæt flueben ved "Alle skal kvittere", hvis du skal kunne se, hvem der har læst det.`,
            `Vælg "Giv besked i appen" - og eventuelt "Send også som SMS", hvis det haster.`,
            `Tryk "Slå op".`
          ]
        },
        {
          title: 'Se hvem der mangler at læse det',
          steps: [
            `Tryk på knappen "3 af 14 har læst" på opslaget.`,
            `Listen viser, hvem der har kvitteret, og hvem der mangler.`
          ]
        }
      ],
      tips: [`Rettes et opslag bagefter, sendes der ikke besked igen.`]
    },
    {
      id: 'intranet-events',
      title: 'Svar på en begivenhed',
      icon: '📅',
      role: 'Medarbejder',
      module: 'intranet_events',
      where: `"Intranet" → "Hverdag" → "Kalender"`,
      intro: `Fester, møder og kurser står her. Er der foreslået flere datoer, siger du, hvornår du kan - så vælger den, der har lavet den, datoen bagefter.`,
      sketch: {
        device: 'phone',
        title: 'Kalender',
        nav: true,
        blocks: [
          {
            t: 'card',
            title: '🎉 Julefrokost',
            children: [
              { t: 'text', text: 'Simon · hele huset · svar senest 15.11.2026' },
              { t: 'text', text: 'fredag 4. december kl. 18.00' },
              { t: 'btns', items: [{ text: 'Kan', primary: true, n: 1 }, { text: 'Måske' }, { text: 'Kan ikke' }] },
              { t: 'field', label: 'Gæster med', value: '1', n: 2 },
              { t: 'btn', text: 'Gem mit svar', primary: true, n: 3 }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Sig om du kommer',
          steps: [
            n(1, `Tryk "Kan", "Måske" eller "Kan ikke" ud for hver dato, der er foreslået.`),
            n(2, `Skriv hvor mange du kommer med ud over dig selv, og en bemærkning hvis der er noget.`),
            n(3, `Tryk "Gem mit svar". Du kan rette det, til fristen er udløbet.`)
          ]
        }
      ],
      tips: [
        `Du kan se, hvad kollegerne har svaret - så kan I selv finde den aften, hvor flest kan.`,
        `Står "Datoen er: ..." øverst, er datoen valgt, og afstemningen er slut.`
      ]
    },
    {
      id: 'intranet-events-create',
      title: 'Opret en fest eller et møde',
      icon: '🎉',
      role: 'Medarbejder',
      module: 'intranet_events',
      where: `"Intranet" → "Kalender" → "Ny begivenhed"`,
      intro: `Alle må oprette - skal holdet holde en fest, skal du ikke først spørge kontoret. Foreslå gerne flere datoer og lad folk krydse af.`,
      flows: [
        {
          title: 'Lav begivenheden',
          steps: [
            `Tryk "Ny begivenhed", og giv den et navn. Vælg om det er en fest, et møde, et kursus eller andet.`,
            `Vælg datoen, og tag klokkeslættet i listen ved siden af - den går i halve timer hele døgnet.`,
            `Skal folk stemme om datoen, så tryk "+ Foreslå en dato mere" og vælg en dato mere.`,
            `Sæt "Svar senest" til den dag, du skal vide det.`,
            `Vælg "Mind om den" - f.eks. 3 dage før - så får alle en besked igen.`,
            `Fjern fluebenet ved "Hele huset", hvis kun nogle skal med, og sæt kryds ved dem.`,
            `Vælg om der skal gives besked i appen, og om den også skal sendes som SMS.`,
            `Tryk "Opret begivenhed".`
          ]
        },
        {
          title: 'Udgiv den senere',
          steps: [
            `Sæt en dato i "Udgives", hvis den først skal ud til folk en anden dag.`,
            `Indtil da kan kun du og kontoret se den. Beskederne går ud af sig selv på dagen.`
          ]
        },
        {
          title: 'Vælg datoen til sidst',
          steps: [
            `Når folk har svaret, trykker du "Vælg denne" ud for den dato, der passer bedst.`,
            `Alle inviterede får besked om, hvornår det bliver - medmindre du har fravalgt både besked i appen og SMS.`
          ]
        }
      ],
      tips: [
        `Vil du prøve noget af uden at forstyrre nogen, så fjern fluebenet ved "Giv besked i appen" (og lad SMS være fra). Så er begivenheden helt stille: ingen besked, når den udgives, når datoen vælges, eller ved påmindelsen.`,
        `"Aflys" lader begivenheden stå med AFLYST, så folk kan se det. "Slet" fjerner den helt.`,
        `Der står altid, hvem der har oprettet den, med profilbillede.`
      ]
    },
    {
      id: 'intranet-tasks',
      title: 'Interne opgaver',
      icon: '✅',
      role: 'Medarbejder',
      module: 'intranet_tasks',
      where: `"Intranet" → "Hverdag" → "Opgaver"`,
      intro: `Husets egne to-do'er, som ikke hører til en kundesag: ryd op på lageret, bestil arbejdstøj, book bilen til syn. Alle må skrive en opgave.`,
      sketch: {
        device: 'phone',
        title: 'Opgaver',
        nav: true,
        blocks: [
          { t: 'btns', items: [{ text: 'Kun mine' }, { text: 'Vis også færdige' }, { text: 'Ny opgave', primary: true, n: 1 }] },
          {
            t: 'card',
            title: 'Ryd op på lageret',
            children: [
              { t: 'text', text: '👥 Serviceholdet · frist 01.10.2026', n: 2 },
              { t: 'btns', items: [{ text: 'Flueben', primary: true, n: 3 }, { text: 'Ret' }] }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Skriv en opgave',
          steps: [
            n(1, `Tryk "Ny opgave", og skriv hvad der skal laves.`),
            n(2, `Vælg hvem der tager den - en kollega eller en arbejdsgruppe - og sæt eventuelt en frist.`),
            `Tryk "Gem". Den, opgaven ligger hos, får en besked.`
          ]
        },
        {
          title: 'Luk en opgave',
          steps: [
            n(3, `Sæt fluebenet, når den er lavet. Fortryder du, tager du det af igen.`),
            `Du kan lukke dine egne opgaver, dem du selv har skrevet, og din arbejdsgruppes.`
          ]
        }
      ],
      tips: [
        `Det her er IKKE sagernes opgaver. En opgave på en sag laves inde på sagen og tæller med i dens fremdrift.`,
        `"Kun mine" viser det, der ligger hos dig eller hos en gruppe, du er med i.`
      ]
    },
    {
      id: 'intranet-apv',
      title: 'Svar på APV\'en',
      icon: '📋',
      role: 'Medarbejder',
      module: 'intranet_apv',
      where: `"Intranet" → "Personale" → "Arbejdsmiljø" → "APV"`,
      intro: `Arbejdspladsvurderingen laves mindst hvert 3. år. Du svarer på et kort spørgeskema - anonymt. Kontoret laver en handlingsplan ud fra svarene.`,
      sketch: {
        device: 'phone',
        title: 'APV',
        nav: true,
        blocks: [
          { t: 'text', text: 'Tunge løft og bæring (kabeltromler, tavler)' },
          { t: 'btn', text: '👍 OK', n: 1 },
          { t: 'btn', text: '⚠ Problem', primary: true, n: 1 },
          { t: 'field', label: 'Hvad er problemet?', value: 'Tromlerne er for tunge alene', n: 2 },
          { t: 'btn', text: 'Send mine svar (anonymt)', primary: true, n: 3 }
        ]
      },
      flows: [
        {
          title: 'Svar',
          steps: [
            n(1, `Tryk "OK", "Problem" eller "Ikke relevant" ved hvert spørgsmål.`),
            n(2, `Ved "Problem" kan du skrive, hvad der er galt - så ved kontoret, hvad der skal gøres.`),
            n(3, `Tryk "Send mine svar". Der gemmes ikke, hvem der har svaret hvad - kun at du har svaret.`)
          ]
        }
      ],
      tips: [`Handlingsplanen står under APV'en. Er du ansvarlig for et punkt, finder du det under fanen "Opgaver".`]
    },
    {
      id: 'intranet-apv-manage',
      title: 'Lav APV\'en',
      icon: '📋',
      role: 'Administrator',
      module: 'intranet_apv',
      where: `"Intranet" → "Personale" → "Arbejdsmiljø" → "APV" → "Start ny APV"`,
      intro: `Kontoret starter runden, ser resultatet og laver handlingsplanen. Du får besked 60 dage før, den næste skal laves.`,
      flows: [
        {
          title: 'Start runden',
          steps: [
            `Tryk "Start ny APV". Standardspørgsmålene står der - ret, slet eller tilføj linjer ("Område: spørgsmål").`,
            `Vælg svarfristen, og tryk "Start APV'en". Alle får en besked.`
          ]
        },
        {
          title: 'Lav handlingsplanen',
          steps: [
            `Under "Resultat" ser du, hvor mange der har svaret, og svarene pr. spørgsmål med kommentarerne.`,
            `Tryk "+ Tilføj punkt", skriv hvad der skal gøres, vælg ansvarlig og frist. Punktet bliver en intern opgave, og den ansvarlige får en påmindelse en uge før fristen.`,
            `Tryk "Afslut APV'en", når handlingsplanen er lagt. Så skal den næste laves senest om 3 år.`
          ]
        }
      ],
      tips: [`Svarene er anonyme - heller ikke kontoret kan se, hvem der har svaret hvad.`]
    },
    {
      id: 'intranet-chemicals',
      title: 'Kemikalier og datablade',
      icon: '🧪',
      role: 'Medarbejder',
      module: 'intranet_chemicals',
      where: `"Intranet" → "Personale" → "Arbejdsmiljø" → "Kemikalier"`,
      intro: `Produkterne, vi bruger - brandskum, lim, spray - med sikkerhedsdatablad og arbejdspladsbrugsanvisning. Læs brugsanvisningen, før du bruger et produkt første gang.`,
      flows: [
        {
          title: 'Slå et produkt op',
          steps: [
            `Søg efter produktet, og tryk "Brugsanvisning" eller "Sikkerhedsdatablad" for at åbne filen.`,
            `Under produktet står, hvilke værnemidler du skal bruge.`
          ]
        }
      ]
    },
    {
      id: 'intranet-chemicals-manage',
      title: 'Vedligehold kemikalielisten',
      icon: '🧪',
      role: 'Administrator',
      module: 'intranet_chemicals',
      where: `"Intranet" → "Personale" → "Arbejdsmiljø" → "Kemikalier" → "+ Tilføj produkt"`,
      intro: `Kontoret holder listen ved lige. Filerne lægges i SharePoint under Intranet/Kemikalier.`,
      flows: [
        {
          title: 'Tilføj et produkt',
          steps: [
            `Tryk "+ Tilføj produkt", og skriv navn, leverandør, hvad det bruges til, hvor det står, og fare og værnemidler.`,
            `Vælg sikkerhedsdatabladet og arbejdspladsbrugsanvisningen som PDF, og skriv databladets dato.`,
            `Tryk "Gem". Bruger I ikke produktet mere, så tryk "Ret" og fjern fluebenet "Vi bruger det stadig".`
          ]
        }
      ],
      tips: [`Du får besked, når et sikkerhedsdatablad bliver 3 år gammelt - så hent et nyt hos leverandøren.`]
    },
    {
      id: 'intranet-incidents',
      title: 'Meld en nærved-hændelse',
      icon: '🦺',
      role: 'Medarbejder',
      module: 'intranet_incidents',
      where: `"Intranet" → "Personale" → "Arbejdsmiljø" - eller "+" → "Meld nærved-hændelse"`,
      intro: `Gik det lige - eller så du noget farligt? Meld det, så det kan blive rettet, før nogen kommer til skade. Du kan melde anonymt.`,
      sketch: {
        device: 'phone',
        title: 'Meld en hændelse',
        nav: true,
        blocks: [
          { t: 'btns', items: [{ text: 'Nærved-ulykke', primary: true, n: 1 }, { text: 'Farlig situation' }, { text: 'Ulykke' }] },
          { t: 'field', label: 'Hvor *', value: 'Industrivej 12, tavlerummet', n: 2 },
          { t: 'field', label: 'Hvad skete der? *', value: 'Stigen skred på det våde gulv', n: 3 },
          { t: 'field', label: 'Billeder', value: '2 valgt' },
          { t: 'text', text: '☐ Meld anonymt', n: 4 },
          { t: 'btn', text: 'Meld hændelsen', primary: true, n: 5 }
        ]
      },
      flows: [
        {
          title: 'Meld en hændelse',
          steps: [
            n(1, `Vælg hvad der er sket: nærved-ulykke, farlig situation eller ulykke.`),
            n(2, `Skriv hvornår og hvor.`),
            n(3, `Skriv hvad der skete - og hvad der kunne være sket. Tag gerne et billede.`),
            n(4, `Sæt flueben i "Meld anonymt", hvis du ikke vil have dit navn på. Så gemmes der intet om dig - men du kan heller ikke se meldingen bagefter.`),
            n(5, `Tryk "Meld hændelsen". Dem, der tager imod, får besked med det samme.`)
          ]
        },
        {
          title: 'Følg op (dem, der tager imod)',
          steps: [
            `Åbn fanen "Arbejdsmiljø". Nye hændelser står øverst med rød kant.`,
            `Tryk "Følg op", vælg status og skriv hvad der er gjort.`,
            `En hændelse kan kun lukkes, når der står, hvad der er gjort ved det. Den, der meldte med navn, får besked.`
          ]
        }
      ],
      tips: [
        `En alvorlig ulykke skal stadig anmeldes til Arbejdstilsynet - meldingen her gør det ikke.`,
        `Vil du være anonym, så skriv ikke noget i teksten, der afslører dig.`,
        `Hvem der tager imod, bestemmes med fluebenet "Tager imod nærved-hændelser" på en arbejdsgruppe. Er der ingen, går hændelserne til kontoret.`
      ]
    },
    {
      id: 'intranet-welcome',
      title: 'Byd en ny kollega velkommen',
      icon: '👋',
      role: 'Administrator',
      module: 'intranet_welcome',
      where: `"Intranet" → "Telefonliste" → "Datoer og velkomst" → "👋 Byd velkommen"`,
      intro: `Med ét tryk: et velkomstopslag med den nyes billede, kontorets tjekliste (nøgler, bil, værktøj, adgang) og den nyes egen tjekliste - som opgaver med frist fra første arbejdsdag. Kontor og administratorer.`,
      flows: [
        {
          title: 'Byd velkommen',
          steps: [
            `Åbn Telefonlisten, tryk "Datoer og velkomst", og tryk "👋 Byd velkommen" ved den nye.`,
            `Vælg "Medarbejder" eller "Lærling" - det er foreslået ud fra rollerne, og punkterne følger valget.`,
            `Skriv første arbejdsdag, hvem kontorets punkter skal ligge hos, og gerne et par ord til opslaget.`,
            `Tryk "Byd velkommen". Opslaget kommer på Nyheder, og opgaverne står under intranettets Opgaver.`
          ]
        },
        {
          title: 'Ret tjeklisten',
          steps: [
            `Tryk "Velkomst-tjekliste" i Telefonlisten.`,
            `Ret et punkt, antal dage til fristen, og om det gælder begge, kun medarbejdere eller kun lærlinge.`,
            `Skriv et nyt punkt nederst, og tryk "Tilføj". ✕ fjerner et punkt.`
          ]
        }
      ],
      tips: [
        `Man kan kun bydes velkommen én gang - derefter står der "👋 Budt velkommen".`,
        `Har den nye ingen ansættelsesdato, bliver første arbejdsdag den - så kommer jubilæerne af sig selv.`,
        `Sæt gerne profilbilledet på den nye først - så kommer det med i opslaget.`
      ]
    },
    {
      id: 'intranet-celebrations',
      title: 'Fødselsdage og jubilæer',
      icon: '🎂',
      role: 'Medarbejder',
      module: 'intranet_celebrations',
      where: `"Mere" → "Min fødselsdag" - og boksen øverst på intranettets Nyheder`,
      intro: `Skriv din fødselsdag, så kollegerne kan sige tillykke. Kun dag og måned - året gemmes ikke. Jubilæer kommer af sig selv fra ansættelsesdatoen.`,
      flows: [
        {
          title: 'Skriv din fødselsdag',
          steps: [
            `Tryk "Mere", og find "Min fødselsdag".`,
            `Vælg dag og måned, og tryk "Gem".`,
            `Vil du ikke have den vist, så fjern fluebenet i "Vis den for kollegerne".`
          ]
        },
        {
          title: 'Ansættelsesdatoer (kontoret)',
          steps: [
            `Åbn intranettets "Telefonliste", og tryk "Datoer og velkomst".`,
            `Skriv datoen ved hver kollega. Den gemmes, når du går ud af feltet.`,
            `Tryk "Færdig". Jubilæerne kommer nu af sig selv hvert år.`
          ]
        }
      ],
      tips: [
        `På dagen står det på forsiden og øverst på TV-skærmen, og alle får en besked kl. 7 - undtagen den, der fejres.`,
        `Boksen på Nyheder viser i dag og de næste 7 dage.`
      ]
    },
    {
      id: 'intranet-oncall',
      title: 'Tilkaldevagt og bytte af vagt',
      icon: '📟',
      role: 'Medarbejder',
      module: 'intranet_oncall',
      where: `"Intranet" → "Hverdag" → "Vagt" - vagten nu står også på forsiden og TV'et`,
      intro: `Hvem har tilkaldevagten, og hvornår har du den? Vagten skifter mandag kl. 7. Kan du ikke tage din uge, så spørg en kollega om at bytte.`,
      sketch: {
        device: 'phone',
        title: 'Vagt',
        nav: true,
        blocks: [
          { t: 'row', text: '📟 Jens Montør', right: '📞 12 34 56 78', n: 1 },
          { t: 'text', text: 'Uge 41 · 05.10-12.10 · Din vagt' },
          { t: 'btn', text: '🔁 Byt', n: 2 },
          { t: 'text', text: 'Mads vil bytte uge 43 med dig.', n: 3 },
          { t: 'btn', text: 'Ja, byt', primary: true }
        ]
      },
      flows: [
        {
          title: 'Ring til vagten',
          steps: [n(1, `Øverst står, hvem der har vagten nu, og hvem der har den næste gang. Tryk på nummeret for at ringe.`)]
        },
        {
          title: 'Byt din vagt',
          steps: [
            n(2, `Tryk "Byt" ud for din uge, og vælg den kollega, du vil spørge.`),
            `Under "Til gengæld" kan du vælge en af kollegaens uger - så bytter I. Ellers tager kollegaen bare ugen.`,
            `Tryk "Spørg kollegaen". Du har vagten, indtil kollegaen siger ja. "Træk tilbage" fortryder spørgsmålet.`,
            n(3, `Bliver du selv spurgt, står det øverst på fanen. Tryk "Ja, byt" eller "Nej" - kontoret får besked, når I har byttet.`)
          ]
        }
      ],
      tips: [
        `Står der en advarsel ved din uge, har du fravær i den uge. Så er det en god idé at bytte.`,
        `En uge, der er slut, kan ikke byttes.`
      ]
    },
    {
      id: 'intranet-oncall-manage',
      title: 'Lav vagtplanen',
      icon: '📟',
      role: 'Administrator',
      module: 'intranet_oncall',
      where: `"Intranet" → "Hverdag" → "Vagt" → "Rækkefølge (kontoret)"`,
      intro: `Kontoret vælger, hvem der går vagt og i hvilken rækkefølge. Systemet lægger rækkefølgen på ugerne et halvt år frem, og hver uge kan rettes.`,
      flows: [
        {
          title: 'Sæt rækkefølgen',
          steps: [
            `Vælg kollegerne under "+ Tilføj en kollega", og flyt dem med ↑ og ↓.`,
            `Tryk "Gem rækkefølgen" og derefter "Fyld planen ud". De næste 26 uger uden vagt får en - de uger, der allerede er sat, bliver stående.`
          ]
        },
        {
          title: 'Ret en enkelt uge',
          steps: [`Vælg en anden kollega i feltet ud for ugen - eller "- ingen -" for at fjerne vagten. Det gemmes med det samme.`]
        }
      ],
      tips: [
        `Fravær (ferie, kursus, sygdom) i ugen står som en advarsel - det spærrer ikke.`,
        `Tryk "Fyld planen ud" igen af og til, så planen altid rækker et halvt år frem.`
      ]
    },
    {
      id: 'intranet-meals',
      title: 'Frokost og morgenbrød',
      icon: '🍽',
      role: 'Medarbejder',
      module: 'intranet_meals',
      where: `"Intranet" → "Hverdag" → "Frokost"`,
      intro: `Når kontoret inviterer til julefrokost eller morgenbrød, melder du dig til her - og skriver, hvis du har særlige behov.`,
      sketch: {
        device: 'phone',
        title: 'Frokost',
        nav: true,
        blocks: [
          { t: 'row', text: '🍽 Julefrokost', right: '11.12 kl. 12' },
          { t: 'field', label: 'Særlige behov', value: 'vegetar', n: 1 },
          { t: 'btn', text: 'Jeg kommer', primary: true, n: 2 },
          { t: 'row', text: '🧺 Hent snaps hos købmanden', right: 'Jeg tager den', n: 3 }
        ]
      },
      flows: [
        {
          title: 'Meld dig til',
          steps: [
            n(1, `Skriv evt. særlige behov, fx "vegetar" eller "uden gluten".`),
            n(2, `Tryk "Jeg kommer". Du kan rette behovet eller trykke "Meld fra", indtil tilmeldingen lukker.`)
          ]
        },
        {
          title: 'Tag tjansen',
          steps: [n(3, `Er der en tjans, fx at hente morgenbrød, så tryk "Jeg tager den". Den første får den - og kan give den fra sig igen.`)]
        }
      ]
    },
    {
      id: 'intranet-meals-manage',
      title: 'Opret et fælles måltid',
      icon: '🍽',
      role: 'Administrator',
      module: 'intranet_meals',
      where: `"Intranet" → "Hverdag" → "Frokost" → "Nyt fælles måltid" - eller "+" → "Fælles måltid"`,
      intro: `Kontoret opretter måltidet, når der er et. Alle får en besked, og når tilmeldingen lukker, får du antallet og de særlige behov.`,
      flows: [
        {
          title: 'Opret måltidet',
          steps: [
            `Skriv hvad det er, hvornår der spises, og hvornår tilmeldingen skal lukke - så du kan nå at bestille.`,
            `Skriv evt. en tjans, fx "Hent morgenbrød hos bageren". Den første, der trykker, tager den.`,
            `Tryk "Opret". Under måltidet ser du, hvem der kommer, og deres særlige behov.`
          ]
        }
      ],
      tips: [`"Aflys" sender en besked til alle, der har meldt sig til.`]
    },
    {
      id: 'intranet-kudos',
      title: 'Ros en kollega',
      icon: '🙌',
      role: 'Medarbejder',
      module: 'intranet_kudos',
      where: `"Intranet" → "Fællesskab" → "Ros" - eller "+" → "Ros en kollega"`,
      intro: `Har en kollega gjort noget godt, så sig det, så alle kan se det. Fanen viser en måned ad gangen og hvem, der er rost mest.`,
      sketch: {
        device: 'phone',
        title: 'Ros',
        nav: true,
        blocks: [
          { t: 'btn', text: '🙌 Ros en kollega', primary: true, n: 1 },
          { t: 'row', text: '🏆 Jens Montør', right: '3 gange', n: 2 },
          { t: 'text', text: '🙌 Mads - tak for hjælpen med tavlen på Vesterbro!' }
        ]
      },
      flows: [
        {
          title: 'Ros en kollega',
          steps: [
            n(1, `Tryk "Ros en kollega", og vælg en eller flere kolleger i listen.`),
            `Skriv, hvad du vil rose for, og tryk "Send rosen". Kollegaen får en besked.`
          ]
        },
        {
          title: 'Se måneden',
          steps: [n(2, `Øverst står, hvem der er rost mest i måneden. Skift måned med ‹ og ›.`)]
        }
      ],
      tips: [`Du kan slette din egen ros med "Slet". Kontoret kan slette al ros.`]
    },
    {
      id: 'intranet-market',
      title: 'Køb, salg og bytte',
      icon: '🛒',
      role: 'Medarbejder',
      module: 'intranet_market',
      where: `"Intranet" → "Fællesskab" → "Køb og salg" - eller "+" → "Køb og salg"`,
      intro: `En opslagstavle for os, der arbejder her: noget til salg, noget du leder efter, noget der gives væk eller kan lånes.`,
      sketch: {
        device: 'phone',
        title: 'Køb og salg',
        nav: true,
        blocks: [
          { t: 'btn', text: 'Nyt opslag', primary: true, n: 1 },
          { t: 'row', text: 'Sælges · Trailer 750 kg', right: '1.500 kr.' },
          { t: 'btn', text: 'Solgt', n: 2 },
          { t: 'btn', text: 'Forlæng 30 dage' }
        ]
      },
      flows: [
        {
          title: 'Sæt noget til salg',
          steps: [
            n(1, `Tryk "Nyt opslag", vælg Sælges, Købes, Gives væk eller Lånes / byttes, og skriv en overskrift og eventuelt en pris.`),
            `Tag op til tre billeder, og tryk "Opret opslaget". Det står i 30 dage.`,
            n(2, `Er det solgt, så tryk "Solgt". Har ingen meldt sig, kan du trykke "Forlæng 30 dage".`)
          ]
        }
      ],
      tips: [`Vil du købe noget, så tryk "Ring" på opslaget.`, `Kontoret kan fjerne opslag, der ikke hører hjemme her.`]
    },
    {
      id: 'intranet-tips',
      title: 'Ugens faglige tip',
      icon: '💡',
      role: 'Medarbejder',
      module: 'intranet_tips',
      where: `"Intranet" → "Fællesskab" → "Faglige tip" - ugens tip står også på TV'et`,
      intro: `Kontoret skriver et kort fagligt tip hver uge. Læs det, og tryk "Jeg har læst det".`,
      sketch: {
        device: 'phone',
        title: 'Faglige tip',
        nav: true,
        blocks: [
          { t: 'text', text: '💡 Ugens tip: Sådan tester du en RCD rigtigt', n: 1 },
          { t: 'btn', text: 'Jeg har læst det', primary: true, n: 2 },
          { t: 'row', text: 'Tidligere tip', right: '▾', n: 3 }
        ]
      },
      flows: [
        {
          title: 'Læs ugens tip',
          steps: [
            n(1, `Ugens tip står øverst på fanen.`),
            n(2, `Tryk "Jeg har læst det", når du har læst det. Så forsvinder tallet ved Intranet.`),
            n(3, `Under "Tidligere tip" ligger alle de gamle - tryk på et for at læse det.`)
          ]
        }
      ]
    },
    {
      id: 'intranet-tips-write',
      title: 'Skriv ugens faglige tip',
      icon: '💡',
      role: 'Administrator',
      module: 'intranet_tips',
      where: `"Intranet" → "Fællesskab" → "Faglige tip" → "Skriv ugens tip" - eller "+" → "Ugens faglige tip"`,
      intro: `Kontoret og administratorerne skriver tippet. Alle får en besked, og det står på TV'et resten af ugen.`,
      flows: [
        {
          title: 'Skriv tippet',
          steps: [
            `Tryk "Skriv ugens tip", skriv en overskrift og selve tippet, og vælg eventuelt et billede.`,
            `Tryk "Udgiv tippet". Det nyeste tip fra denne uge er ugens tip.`,
            `"Ret" og "Slet" står under tippet. Under tippet ser du, hvor mange der har læst det.`
          ]
        }
      ],
      tips: [`TV'et viser kun overskriften og begyndelsen af teksten - hold den første sætning kort.`]
    },
    {
      id: 'intranet-meetings',
      title: 'Personalemøder',
      icon: '🗓',
      role: 'Medarbejder',
      module: 'intranet_meetings',
      where: `"Intranet" → "Personale" → "Møder"`,
      intro: `Kontoret indkalder til møder. Alle kan sætte punkter på dagsordenen, og efter mødet kommer referatet, som du kvitterer for.`,
      sketch: {
        device: 'phone',
        title: 'Møder',
        nav: true,
        blocks: [
          { t: 'text', text: '🗓 Personalemøde oktober', n: 1 },
          { t: 'row', text: '1. Nye arbejdsjakker', meta: 'Jens' },
          { t: 'field', label: 'Punkt til dagsordenen', value: 'Parkering ved værkstedet', n: 2 },
          { t: 'btn', text: '+ Sæt på dagsordenen', primary: true },
          { t: 'btn', text: 'Jeg har læst det', n: 3 }
        ]
      },
      flows: [
        {
          title: 'Før og efter mødet',
          steps: [
            n(1, `Du får en besked, når kontoret indkalder - og igen dagen før, med dagsordenen.`),
            n(2, `Skriv dit punkt, og tryk "Sæt på dagsordenen". Det kommer på med det samme. Du kan fjerne dine egne punkter igen.`),
            n(3, `Når referatet er udgivet, får du en besked. Læs det, og tryk "Jeg har læst det".`)
          ]
        }
      ],
      tips: [
        `Et møde for en arbejdsgruppe ses kun af gruppens medlemmer og kontoret.`,
        `Blev du ansvarlig for en beslutning, ligger den som intern opgave under fanen "Opgaver".`
      ]
    },
    {
      id: 'intranet-meetings-admin',
      title: 'Indkald til møde og skriv referat',
      icon: '📝',
      role: 'Administrator',
      module: 'intranet_meetings',
      where: `"Intranet" → "Personale" → "Møder" → "Nyt personalemøde" - eller "+" → "Personalemøde"`,
      intro: `Kontoret og administratorerne indkalder, ordner dagsordenen og skriver referatet. Når referatet udgives, låses det, og beslutninger med en ansvarlig bliver til interne opgaver.`,
      flows: [
        {
          title: 'Indkald',
          steps: [
            `Tryk "Nyt personalemøde", skriv overskrift, tidspunkt og sted, og vælg om det er for hele huset eller en arbejdsgruppe.`,
            `Tryk "Indkald". Deltagerne får besked og kan sætte punkter på.`,
            `Flyt punkterne med ↑ og ↓, og fjern dem, der ikke skal med.`
          ]
        },
        {
          title: 'Referatet',
          steps: [
            `Tryk "Skriv referat". Skriv fælles noter, noter pr. punkt og beslutningerne.`,
            `Vælg en ansvarlig og evt. en frist på de beslutninger, der skal gøres noget ved.`,
            `Tryk "Gem kladden". Kladden ses kun af kontoret og kan rettes.`,
            `Tryk "Udgiv referatet", når mødet er holdt. Deltagerne får besked, og de ansvarlige får en intern opgave.`,
            `Under referatet ser du, hvor mange der har læst det, og hvem der mangler.`
          ]
        }
      ],
      tips: [
        `Et udgivet referat kan ikke rettes. Skriv et opslag, hvis der skal rettes noget bagefter.`,
        `En arbejdsgruppe med møder kan ikke slettes - sæt den inaktiv i stedet.`,
        `"Aflys" giver deltagerne besked.`
      ]
    },
    {
      id: 'intranet-mus',
      title: 'MUS-samtale',
      icon: '💬',
      role: 'Medarbejder',
      module: 'intranet_mus',
      where: `"Intranet" → "Personale" → "MUS"`,
      intro: `Din MUS-samtale er fortrolig: forberedelse, referat og aftaler ser KUN du og din leder. Kontoret ser kun, hvornår samtalen er.`,
      sketch: {
        device: 'phone',
        title: 'MUS med Pia',
        nav: true,
        blocks: [
          { t: 'field', label: 'Hvordan har det seneste år været for dig?', value: 'Godt - mange nye kunder', n: 1 },
          { t: 'btn', text: 'Klar', primary: true, n: 2 },
          { t: 'row', text: 'Referat - læs og godkend', n: 3 },
          { t: 'btn', text: 'Godkend referatet', primary: true }
        ]
      },
      flows: [
        {
          title: 'Før og efter samtalen',
          steps: [
            n(1, `Du får en besked, når samtalen er planlagt - og igen 3 dage før. Skriv dine svar under "Forberedelse", og tryk "Gem" undervejs.`),
            n(2, `Tryk "Klar", når du er færdig. Så kan du ikke rette mere. I ser hinandens svar, når I begge har trykket "Klar".`),
            n(3, `Efter samtalen skriver lederen referatet. Du får en besked, læser det, og trykker "Godkend referatet" - evt. med en kommentar.`)
          ]
        }
      ],
      tips: [
        `Aftaler med en ansvarlig bliver private opgaver under "Opgaver" (🔒) - kun I to ser dem.`,
        `Passer noget ikke i referatet, så skriv det i kommentaren, eller tal med lederen før du godkender.`
      ]
    },
    {
      id: 'intranet-mus-leader',
      title: 'Planlæg og hold MUS-samtaler',
      icon: '💬',
      role: 'Projektleder',
      module: 'intranet_mus',
      where: `"Intranet" → "Personale" → "MUS" → "Planlæg MUS-samtale" - eller "+" → "MUS-samtale"`,
      intro: `Kontoret planlægger og vælger lederen; en leder kan selv planlægge sine egne. Kontoret ser en oversigt over, hvem der mangler - men aldrig indholdet.`,
      flows: [
        {
          title: 'Planlæg',
          steps: [
            `Tryk "Planlæg MUS-samtale", vælg medarbejder, leder (projektleder eller derover), tidspunkt og sted, og tryk "Planlæg".`,
            `Begge får besked. Spørgsmålene kopieres ind fra listen - en senere rettelse ændrer ikke samtalen.`,
            `Under "Oversigt for kontoret" står dem, der ikke har haft MUS det sidste år. Tryk "Planlæg" ud for dem, eller "Flyt" og "Aflys" ud for en planlagt.`
          ]
        },
        {
          title: 'Som leder',
          steps: [
            `Forbered dig på dine egne spørgsmål, og tryk "Klar".`,
            `Efter samtalen: skriv referatet og aftalerne. Vælg en ansvarlig (medarbejderen eller dig) og evt. en frist.`,
            `Tryk "Gem kladden" undervejs - kun du ser den. Tryk "Send til ..." når den er færdig.`,
            `Når medarbejderen har godkendt, låses samtalen, og aftalerne bliver private opgaver.`
          ]
        },
        {
          title: 'Ret spørgsmålene (kontoret)',
          steps: [`Tryk "Ret" ved "Spørgsmål til forberedelsen", ret, tilføj eller fjern, og tryk "Gem spørgsmålene".`]
        }
      ],
      tips: [
        `Kontoret får et varsel, når en medarbejders sidste samtale er ved at være et år gammel - kun for dem, der har haft en.`,
        `Der kan kun være én åben samtale pr. medarbejder.`
      ]
    },
    {
      id: 'intranet-apprentice',
      title: 'Lærlingeplan og logbog',
      icon: '🎓',
      role: 'Medarbejder',
      module: 'intranet_apprentices',
      where: `"Intranet" → "Personale" → "Lærling" - kun for lærlinge, deres svende og kontoret`,
      intro: `Planen viser dine praktikmål, dine skoleperioder og din logbog. Den ses kun af dig, din ansvarlige svend og kontoret.`,
      sketch: {
        device: 'phone',
        title: 'Lærling',
        nav: true,
        blocks: [
          { t: 'row', text: '🏫 Skole 02.11 - 18.12', n: 1 },
          { t: 'row', text: '☑ Opbygge en gruppetavle', meta: 'Svend', n: 2 },
          { t: 'field', label: 'Hvad lavede du i dag?', value: 'Trak kabler i villaen', n: 3 },
          { t: 'btn', text: 'Gem indlægget', primary: true }
        ]
      },
      flows: [
        {
          title: 'Som lærling',
          steps: [
            n(1, `Dine skoleperioder står øverst - de står også i kalenderen som "Skole".`),
            n(2, `Praktikmålene krydses af af din svend, når du har vist, at du kan det.`),
            n(3, `Skriv i logbogen: dato, hvad du lavede, evt. sagen, praktikmålet og op til 3 billeder. Du kan slette dine egne indlæg.`)
          ]
        },
        {
          title: 'Som svend',
          steps: [
            `Du får en besked, når du bliver ansvarlig for en lærling.`,
            `Sæt flueben ved et praktikmål, når lærlingen kan det - evt. med en kommentar. Lærlingen får besked.`,
            `Tryk "Kommentér" under et indlæg i logbogen for at give lærlingen en tilbagemelding.`
          ]
        }
      ],
      tips: [`Du får en besked, når svenden krydser et mål af eller kommenterer din logbog.`]
    },
    {
      id: 'intranet-apprentice-admin',
      title: 'Opret en lærlingeplan',
      icon: '🎓',
      role: 'Administrator',
      module: 'intranet_apprentices',
      where: `"Intranet" → "Personale" → "Lærling" → "Ny lærlingeplan" - eller "+" → "Lærlingeplan"`,
      intro: `Kontoret opretter planen, vælger den ansvarlige svend og retter husets liste over praktikmål.`,
      flows: [
        {
          title: 'Opret',
          steps: [
            `Tryk "Ny lærlingeplan", vælg lærlingen og den ansvarlige svend, og skriv start og forventet svendeprøve.`,
            `Praktikmålene kopieres ind fra husets liste. Tilføj ekstra mål til netop denne lærling under praktikmålene.`,
            `Skriv skoleperioderne som fravær af typen "Skole" i kalenderen - så advarer planlægningen, hvis lærlingen sættes på en sag.`,
            `Tryk "Afslut læretiden", når svendeprøven er bestået. Planen bliver stående som historik.`
          ]
        },
        {
          title: 'Husets praktikmål',
          steps: [
            `Tryk "Ret" ved "Husets praktikmål". Listen er et EKSEMPEL - ret den efter uddannelsesordningen.`,
            `Tryk "Gem listen". Kun nye planer får ændringen.`
          ]
        }
      ],
      tips: [`Kun én aktiv plan pr. lærling.`]
    },
    {
      id: 'intranet-week-summary',
      title: 'Ugen der gik',
      icon: '📰',
      role: 'Medarbejder',
      module: 'intranet_week_summary',
      where: `"Intranet" → "Hverdag" → "Nyheder" - fredag kl. 14. Står også på TV'et resten af fredagen`,
      intro: `Hver fredag kl. 14 lægger Overblik selv et opslag på intranettet med ugens tal og det, der sker i næste uge. Alle får en besked. Ingen skal skrive noget.`,
      sketch: {
        device: 'phone',
        title: 'Nyheder',
        nav: true,
        blocks: [
          { t: 'text', text: '📰 Ugen der gik - uge 40', n: 1 },
          { t: 'row', text: '5 sager afsluttet · 3 servicebesøg', n: 2 },
          { t: 'row', text: 'Næste uge: vagt, begivenheder, frokost, ferie', n: 3 }
        ]
      },
      flows: [
        {
          title: 'Hvad står der',
          steps: [
            n(1, `Opslaget har overskriften "Ugen der gik" og ugenummeret. Afsenderen er "Overblik".`),
            n(2, `Ugen der gik: afsluttede sager og servicesager, gennemførte servicebesøg og nye sager fra mandag til fredag kl. 14.`),
            n(3, `Næste uge: hvem der har vagt, husets fælles begivenheder, måltider og hvem der holder ferie.`)
          ]
        }
      ],
      tips: [
        `Kun ferie kommer med - aldrig sygdom eller andet fravær.`,
        `TV'et viser kun tallene, vagten og det fælles - ikke hvem der har ferie.`,
        `Superadmin kan slå den fra under Administration → Moduler → Intranet.`
      ]
    },
    {
      id: 'intranet-polls',
      title: 'Hurtige afstemninger',
      icon: '📊',
      role: 'Medarbejder',
      module: 'intranet_polls',
      where: `"Intranet" → "Fællesskab" → "Afstemninger" - eller "+" → "Ny afstemning"`,
      intro: `Spørg huset - eller din arbejdsgruppe - om noget: "Pizza eller burger fredag?". Resultatet står live, og alle kan stemme fra telefonen.`,
      sketch: {
        device: 'phone',
        title: 'Afstemninger',
        nav: true,
        blocks: [
          { t: 'row', text: '📊 Pizza eller burger fredag?', right: 'Åben' },
          { t: 'btn', text: '● Pizza · 7 · 64 %', primary: true, n: 1 },
          { t: 'btn', text: '○ Burger · 4 · 36 %' },
          { t: 'text', text: '11 har stemt · tryk igen for at ændre din stemme', n: 2 }
        ]
      },
      flows: [
        {
          title: 'Stem',
          steps: [
            n(1, `Tryk på det svar, du vil stemme på. Er flere svar tilladt, kan du sætte kryds ved flere.`),
            n(2, `Vil du ændre din stemme, så tryk på et andet svar. Tryk på dit eget igen for at trække stemmen tilbage.`)
          ]
        },
        {
          title: 'Start en afstemning',
          steps: [
            `Tryk "Ny afstemning", skriv spørgsmålet og mindst to svar ("+ Tilføj svar" giver flere).`,
            `Vælg eventuelt en slutdato og en arbejdsgruppe - så er den kun til gruppens medlemmer.`,
            `Sæt flueben i "Man må sætte kryds ved flere svar" og/eller "Hemmelig afstemning", hvis det skal være sådan.`,
            `Tryk "Start afstemningen". Alle, den er til, får en besked.`
          ]
        }
      ],
      tips: [
        `En hemmelig afstemning viser kun tallene - ikke hvem der stemte hvad.`,
        `Du kan lukke din egen afstemning før tid med "Luk afstemningen". Så står vinderen med 🏆.`
      ]
    },
    {
      id: 'intranet-ideas',
      title: 'Idéboksen',
      icon: '💡',
      role: 'Medarbejder',
      module: 'intranet_ideas',
      where: `"Intranet" → "Fællesskab" → "Idéboks" - eller "+" → "Ny idé"`,
      intro: `Har du en idé til at gøre noget bedre - på lageret, i bilerne, i måden vi arbejder på? Læg den i boksen. Alle kan se idéerne og ledelsens svar. Du kan gøre det anonymt.`,
      sketch: {
        device: 'phone',
        title: 'Ny idé',
        nav: true,
        blocks: [
          { t: 'field', label: 'Overskrift *', value: 'Fælles værktøjsvogn på lageret', n: 1 },
          { t: 'field', label: 'Hvad går idéen ud på? *', value: 'En vogn med de mest brugte ting, så vi ikke leder', n: 2 },
          { t: 'text', text: '☐ Læg den anonymt', n: 3 },
          { t: 'btn', text: 'Læg den i boksen', primary: true, n: 4 }
        ]
      },
      flows: [
        {
          title: 'Læg en idé i boksen',
          steps: [
            n(1, `Tryk "Ny idé", og giv den en kort overskrift.`),
            n(2, `Skriv hvad idéen går ud på, og hvad den ville gøre bedre.`),
            n(3, `Sæt flueben i "Læg den anonymt", hvis du ikke vil have dit navn på.`),
            n(4, `Tryk "Læg den i boksen". Ledelsen får besked.`)
          ]
        },
        {
          title: 'Svar på en idé (kontoret og administratorer)',
          steps: [
            `Tryk "Svar" ved idéen.`,
            `Vælg Undersøges, Vi gør det, Gennemført eller Ikke nu, og skriv svaret. "Ikke nu" kræver en begrundelse.`,
            `Den, der skrev idéen med navn, får besked. Alle kan se svaret i boksen.`
          ]
        }
      ],
      tips: [
        `En anonym idé kan du stadig se i boksen - men du får ikke besked, når der er svaret.`,
        `Du kan slette din egen idé, indtil ledelsen har svaret.`
      ]
    },
    {
      id: 'intranet-documents',
      title: 'Personalehåndbog og dokumenter',
      icon: '📄',
      role: 'Medarbejder',
      module: 'intranet_documents',
      where: `"Intranet" → "Info" → "Håndbog"`,
      intro: `Personalehåndbogen, politikker, blanketter og referater står her på hylder. Tryk "Hent", så åbner dokumentet på telefonen.`,
      sketch: {
        device: 'phone',
        title: 'Håndbog',
        nav: true,
        blocks: [
          {
            t: 'card',
            title: 'Personalehåndbog',
            children: [
              { t: 'row', text: '📄 Personalehåndbog 2026', meta: 'Hent', n: 1 },
              { t: 'btn', text: 'Jeg har læst det', primary: true, n: 2 }
            ]
          }
        ]
      },
      flows: [
        {
          title: 'Hent et dokument',
          steps: [
            `Åbn "Intranet", og tryk på fanen "Håndbog".`,
            n(1, `Tryk "Hent" ud for det, du skal bruge. Er dokumentet et link, står der "Åbn" i stedet.`),
            n(2, `Står der "Jeg har læst det", så tryk på knappen, når du har læst det.`)
          ]
        }
      ],
      tips: [
        `Står der ingen knap ud for et dokument, er filen ikke lagt op endnu. Sig det til kontoret.`,
        `Under linjen står, hvornår det sidst blev opdateret, og af hvem.`,
        `Er dokumentet lagt på en arbejdsgruppe, er det kun gruppens medlemmer, der kan se det.`
      ]
    },
    {
      id: 'intranet-documents-manage',
      title: 'Læg et dokument op',
      icon: '📤',
      role: 'Administrator',
      module: 'intranet_documents',
      where: `"Intranet" → "Håndbog" → "Nyt dokument"`,
      intro: `Læg personalehåndbogen og de andre dokumenter op, så alle kan hente dem på telefonen. Et dokument kan også bare være et link.`,
      flows: [
        {
          title: 'Læg personalehåndbogen op',
          steps: [
            `Tryk "Nyt dokument", og skriv navnet i "Navn".`,
            `Skriv hylden - f.eks. "Personalehåndbog". Har du brugt hylden før, kan du vælge den i listen.`,
            `Under "Hvem er det til?" står "Hele huset". Vælg en arbejdsgruppe, hvis det kun angår dem.`,
            `Tryk "Vælg fil", og find filen.`,
            `Sæt flueben ved "Alle skal kvittere for at have læst det", hvis alle skal bekræfte det.`,
            `Tryk "Gem".`
          ]
        },
        {
          title: 'Skift filen ud',
          steps: [
            `Tryk "Ret" ud for dokumentet, og vælg den nye fil.`,
            `Tryk "Gem". Den gamle udgave bliver gemt, så man altid kan se, hvad der stod før.`
          ]
        },
        {
          title: 'Et link i stedet for en fil',
          steps: [
            `Lad "Fil" stå tom, og skriv adressen i "... eller et link".`,
            `Adressen skal starte med https:// - ellers siger systemet fra.`
          ]
        }
      ],
      tips: [
        `"Rækkefølge" bestemmer, hvor dokumentet står på hylden. Lavest tal står først.`,
        `Fjern fluebenet ved "Vises for alle" for at tage et dokument ned uden at slette det.`,
        `Kræver dokumentet en kvittering, kan du trykke "3 af 14 har læst" og se, hvem der mangler.`,
        `Et slettet dokument kan hentes frem igen i et stykke tid - sig til, hvis det sker ved et uheld.`
      ]
    },
    {
      id: 'intranet-groups',
      title: 'Arbejdsgrupper',
      icon: '👥',
      role: 'Medarbejder',
      module: 'intranet',
      where: `"Intranet" → "Info" → "Arbejdsgrupper"`,
      intro: `Husets hold: serviceholdet, tavleafdelingen, festudvalget. Her kan du se, hvem der er med i hvad, og hvor mange åbne opgaver holdet har.`,
      tips: [
        `Er du med i en gruppe, ser du dens opslag og får dens beskeder.`,
        `Arbejdsgrupper er ikke det samme som Teams i Microsoft 365 - de rører ikke sagernes filer.`
      ]
    },
    {
      id: 'intranet-groups-manage',
      title: 'Opret en arbejdsgruppe',
      icon: '🧑‍🤝‍🧑',
      role: 'Administrator',
      module: 'intranet',
      where: `"Intranet" → "Arbejdsgrupper" → "Ny arbejdsgruppe"`,
      intro: `Saml et hold under et navn, så det kan inviteres samlet og få sine egne opslag.`,
      flows: [
        {
          title: 'Lav gruppen',
          steps: [
            `Tryk "Ny arbejdsgruppe", og giv den et navn og gerne et ikon.`,
            `Vælg en formand - han bliver automatisk medlem.`,
            `Sæt flueben ved medlemmerne, og tryk "Gem".`
          ]
        },
        {
          title: 'Brug gruppen',
          steps: [
            `Et opslag: vælg gruppen under "Hvem er det til?" - så ser kun medlemmerne det.`,
            `En begivenhed: fjern fluebenet ved "Hele huset", og sæt kryds ved gruppen.`,
            `En opgave: vælg gruppen under "Hvem tager den?" - så kan alle i holdet lukke den.`,
            `Nærved-hændelser: sæt flueben i "Tager imod nærved-hændelser" - så får medlemmerne besked og følger op.`
          ]
        }
      ],
      tips: [`Sletter du en gruppe, bliver dens opslag og opgaver stående og gælder hele huset.`]
    },
    {
      id: 'intranet-phones',
      title: 'Find en kollegas nummer',
      icon: '📞',
      role: 'Medarbejder',
      module: 'intranet',
      where: `"Intranet" → "Info" → "Telefonliste"`,
      intro: `Alle kolleger med billede, mail og mobilnummer. Tryk på nummeret for at ringe op.`,
      flows: [
        {
          title: 'Ring til en kollega',
          steps: [`Skriv et par bogstaver af navnet i søgefeltet.`, `Tryk på nummeret - telefonen ringer op.`]
        }
      ],
      tips: [`Numrene kommer fra Microsoft 365. Står der "Intet nummer", mangler det i 365 og rettes af kontoret.`]
    },
    {
      id: 'intranet-links',
      title: 'Genveje til de andre systemer',
      icon: '🔗',
      role: 'Administrator',
      module: 'intranet_links',
      where: `"Intranet" → "Info" → "Genveje"`,
      intro: `Læg links til Ordrestyring, webmail, lønnen og andet, som folk skal kunne finde uden at lede.`,
      flows: [
        {
          title: 'Læg en genvej op',
          steps: [
            `Tryk "Ny genvej".`,
            `Skriv navnet og hele adressen - den skal starte med https://.`,
            `Skriv gerne et ikon (f.eks. 📋) og en linje om, hvad det er.`,
            `"Rækkefølge" bestemmer, hvor den står. Lavest tal står først.`,
            `Tryk "Gem".`
          ]
        }
      ],
      tips: [`Fjern fluebenet ved "Vises for alle", hvis genvejen skal gemmes uden at blive slettet.`]
    }
  ]
}
